<template>
  <div class="LKPublicationsFormdataOcrResultsTable">
    <div class="d-flex align-center" style="margin-bottom: 12px;">
      <div class="text-h6 flex-grow-1 ml-2">
        <v-btn
          v-if="taskId"
          icon
          @click="onBack"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        Результаты распознавания
      </div>
    </div>

    <div style="display: flex; align-items: flex-start; gap: 12px; margin-bottom: 5px;">
      <div style="width: 200px; min-width: 200px;">
        <v-select
          v-model="filters.status"
          :items="statusOptions"
          label="Фильтр по статусу"
          clearable
          dense
          outlined
          hide-details
          @change="onFilterChange"
        />
      </div>
      <div style="width: 180px; min-width: 180px;">
        <v-text-field
          :value="filters.participant_code"
          label="Код участника"
          clearable
          dense
          outlined
          hide-details
          @input="onParticipantCodeInput"
          @click:clear="onParticipantCodeClear"
        />
      </div>
      <div ref="errorFilterWrapper" style="flex: 1; min-width: 0;">
        <v-autocomplete
          v-model="filters.error_messages"
          :items="errorMessageOptions"
          :loading="isLoadingErrorMessages"
          :menu-props="errorMenuWidth ? { maxWidth: errorMenuWidth } : {}"
          label="Фильтр по комментарию"
          prepend-inner-icon="mdi-filter-variant"
          multiple
          clearable
          dense
          outlined
          hide-details
          no-data-text="Нет доступных комментариев"
          @focus="onErrorFilterFocus"
          @change="onFilterChange"
          @click:clear="onErrorMessageClear"
        >
          <template #item="{ item, on, attrs }">
            <v-list-item v-bind="attrs" v-on="on">
              <v-list-item-action>
                <v-checkbox :input-value="attrs.inputValue" />
              </v-list-item-action>
              <v-list-item-content style="white-space: normal; word-break: break-word;">
                {{ item }}
              </v-list-item-content>
            </v-list-item>
          </template>
          <template #selection="{ index }">
            <span
              v-if="index === 0"
              style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block; max-width: 100%;"
            >
              <template v-if="filters.error_messages.length === 1">{{ filters.error_messages[0] }}</template>
              <template v-else>Выбрано: {{ filters.error_messages.length }}</template>
            </span>
          </template>
        </v-autocomplete>
        <div
          v-if="filters.error_messages.length > 1"
          style="display: flex;
        flex-wrap: wrap; gap: 4px; margin-top: 6px;"
        >
          <v-tooltip
            v-for="msg in filters.error_messages"
            :key="msg"
            bottom
          >
            <template #activator="{ on, attrs }">
              <v-chip
                small
                close
                style="max-width: 220px;"
                v-bind="attrs"
                v-on="on"
                @click:close="onRemoveErrorMessage(msg)"
              >
                <span style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">{{ msg }}</span>
              </v-chip>
            </template>
            <span>{{ msg }}</span>
          </v-tooltip>
        </div>
      </div>
      <div style="padding-top: 3px;">
        <v-tooltip bottom :disabled="selected.length > 0">
          <template #activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-btn
                color="primary"
                outlined
                :disabled="selected.length === 0"
                @click="onMoveToForm"
              >
                Переместить в форму
              </v-btn>
            </div>
          </template>
          <span>Необходимо выбрать хотя бы один результат в таблице</span>
        </v-tooltip>
      </div>
    </div>

    <v-data-table
      v-model="selected"
      show-select
      must-sort
      item-key="ocr_result_id"
      :headers="headers"
      :items="list"
      :loading="isLoading"
      :options.sync="options"
      :server-items-length="pagination.count"
      :footer-props="{
        itemsPerPageOptions: [10, 25, 100, 300],
        showFirstLastPage: true,
      }"
      class="elevation-1"
      :item-class="getRowClass"
    >
      <template #header.data-table-select="{ on, attrs }">
        <v-simple-checkbox
          v-bind="attrs"
          :value="allSelectableSelected"
          v-on="on"
          @input="allSelectableSelected = $event"
        />
      </template>

      <template #item.participant_code="{ item }">
        <v-edit-dialog
          v-if="item.status === 'ERROR' && item.participant_code && item.parts"
          large
          persistent
          save-text="Сохранить"
          cancel-text="Отмена"
          @open="editingCodeValue = String(item.participant_code)"
          @save="onSaveParticipantCode(item)"
          @cancel="editingCodeValue = ''"
        >
          <span class="participant-code-trigger">
            <strong>{{ item.participant_code }} </strong>
            <v-icon x-small class="participant-code-edit-icon ml-4">
              mdi-pencil
            </v-icon>
          </span>
          <template #input>
            <v-text-field
              v-model="editingCodeValue"
              label="Код участника"
              type="number"
              single-line
              autofocus
            />
          </template>
        </v-edit-dialog>
        <strong v-else>{{ item.participant_code }}</strong>
      </template>

      <template #item.created_at="{ item }">
        {{ formatDate(item.created_at) }}
      </template>

      <template #item.status="{ item }">
        <div style="display: inline-flex; align-items: center;">
          <v-icon
            small
            :color="getStatusColor(item.status)"
            class="mr-1"
          >
            {{ getStatusIcon(item.status) }}
          </v-icon>
          <span :class="`${getStatusColor(item.status)}--text font-weight-medium`">
            {{ getStatusText(item.status) }}
          </span>
        </div>
      </template>

      <template #item.parts_count="{ item }">
        <span v-if="item.parts && item.parts.length > 1">
          {{ item.parts.length }}
        </span>
        <span v-else>—</span>
      </template>

      <template #item.error_message="{ item }">
        <span v-if="item.error_message" class="error--text">
          {{ item.error_message }}
        </span>
        <span v-else>—</span>
      </template>

      <template #item.view="{ item }">
        <v-btn
          color="primary"
          small
          outlined
          @click="onViewDetails(item)"
        >
          Просмотр данных
        </v-btn>
      </template>

      <template #header.delete="{}">
        <v-tooltip bottom :disabled="!isAllSelectableRowsSelected">
          <template #activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-btn
                icon
                small
                color="error"
                :disabled="!isAllSelectableRowsSelected"
                @click="onDeleteSelected"
              >
                <v-icon small>
                  mdi-trash-can
                </v-icon>
              </v-btn>
            </div>
          </template>
          <span v-if="isAllSelectableRowsSelected">Удалить все {{ selected.length }} результатов</span>
          <span v-else>Выберите все строки для удаления</span>
        </v-tooltip>
      </template>

      <template #item.delete="{ item }">
        <v-btn
          icon
          color="error"
          small
          :disabled="item.status === 'INSERTED'"
          @click="onDeleteResult(item)"
        >
          <v-icon small>
            mdi-delete
          </v-icon>
        </v-btn>
      </template>

      <template #no-data>
        <div class="text-center pa-4">
          Нет результатов распознавания
        </div>
      </template>
    </v-data-table>

    <LKPublicationsFormdataUploadModal
      v-model="showTransferModal"
      :message="transferMessage"
      :progress="transferProgress"
    />
  </div>
</template>

<script>
import { CollectorOcrApi } from '@/api/resources/Collector/CollectorOcrApi'
import { LkFormdataApi } from '@/api/resources/LkFormdataApi'
import { dateFormatter } from '@/helpers/dateFormatter'
import { createModalConfirm } from '@/helpers/createModalConfirm'
import { createLKPublicationsFormdataTableEditModal } from '@/views/LKPublications/formdata/utils'
import LKPublicationsFormdataUploadModal from './LKPublicationsFormdataUploadModal.vue'
import { Ripple } from 'vuetify/lib/directives'

export default {
  name: 'LKPublicationsFormdataOcrResultsTable',
  directives: { Ripple },
  components: {
    LKPublicationsFormdataUploadModal,
  },
  props: {
    formDataId: {
      type: [String, Number],
      required: true,
    },
    publicationId: {
      type: [String, Number],
      required: true,
    },
    taskId: {
      type: String,
      required: false,
      default: null,
    },
    all_cells: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    errorMenuWidth: null,
    isLoading: false,
    list: [],
    pagination: {
      page: 1,
      per_page: 10,
      count: 0,
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['created_at'],
      sortDesc: [true],
    },
    filters: {
      status: null,
      participant_code: null,
      error_messages: [],
    },
    selected: [],
    errorMessageOptions: [],
    isLoadingErrorMessages: false,
    debounceTimeout: null,
    pollingTimer: null,
    editingCodeValue: '',
    showTransferModal: false,
    transferMessage: 'Перенос данных в форму...',
    transferProgress: 0,
    statusOptions: [
      { text: 'Все', value: null },
      { text: 'Успешно', value: 'SUCCESS' },
      { text: 'Предупреждение', value: 'ERROR' },
      { text: 'Перенесено в форму', value: 'INSERTED' },
    ],
    headers: [
      {
        text: 'Код участника',
        value: 'participant_code',
        sortable: true,
      },
      {
        text: 'Дата создания',
        value: 'created_at',
        sortable: true,
      },
      {
        text: 'Статус',
        value: 'status',
        sortable: false,
      },
      {
        text: 'Количество частей',
        value: 'parts_count',
        sortable: false,
      },
      {
        text: 'Комментарий',
        value: 'error_message',
        sortable: false,
      },
      {
        text: '',
        value: 'view',
        sortable: false,
        align: 'right',
        width: '180px',
      },
      {
        text: '',
        value: 'delete',
        sortable: false,
        align: 'center',
        width: '60px',
      },
    ],
  }),
  computed: {
    isAllSelectableRowsSelected() {
      // Получаем все строки, которые можно выбрать (без INSERTED)
      const selectableRows = this.list.filter((item) => item.status !== 'INSERTED')

      // Проверяем, выбраны ли все доступные строки
      if (selectableRows.length === 0 || this.selected.length === 0) {
        return false
      }

      return selectableRows.every((item) => this.selected.some((sel) => sel.ocr_result_id === item.ocr_result_id))
    },
    allSelectableSelected: {
      get() {
        const selectableRows = this.list.filter((item) => item.status !== 'INSERTED')
        if (selectableRows.length === 0) {
          return false
        }
        return selectableRows.every((item) => this.selected.some((sel) => sel.ocr_result_id === item.ocr_result_id))
      },
      set(value) {
        if (value) {
          // Выбираем все выбираемые строки
          const selectableRows = this.list.filter((item) => item.status !== 'INSERTED')
          this.selected = [...selectableRows]
        } else {
          // При повторном нажатии - снимаем выделение со всех
          this.selected = []
        }
      },
    },
  },
  watch: {
    options: {
      handler() {
        this.loadData()
      },
      deep: true,
    },
    selected(newVal) {
      // Блокируем выделение строк со статусом INSERTED
      const hasInserted = newVal.some((item) => item.status === 'INSERTED')
      if (hasInserted) {
        // Фильтруем INSERTED строки и обновляем selected
        this.selected = newVal.filter((item) => item.status !== 'INSERTED')
      }
    },
  },
  mounted() {
    this.loadData()
    this.startPolling()
  },
  beforeDestroy() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }
    this.stopPolling()
  },
  methods: {
    startPolling() {
      this.pollingTimer = setInterval(() => {
        if (!this.isLoading) {
          this.loadData()
        }
      }, 5000)
    },
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer)
        this.pollingTimer = null
      }
    },
    async loadData() {
      if (!this.formDataId || !this.publicationId) {
        return
      }

      this.isLoading = true
      const api = new CollectorOcrApi()
      try {
        const params = {
          page: this.options.page,
          per_page: this.options.itemsPerPage,
        }

        // Добавляем task_id только если он передан
        if (this.taskId) {
          params.task_id = this.taskId
        }

        if (this.filters.status) {
          params.status = this.filters.status
        }
        if (this.filters.participant_code) {
          params.participant_code = parseInt(this.filters.participant_code, 10)
        }
        if (this.filters.error_messages && this.filters.error_messages.length > 0) {
          params.error_messages = this.filters.error_messages
        }

        // Добавляем сортировку
        if (this.options.sortBy && this.options.sortBy.length > 0) {
          const sortField = this.options.sortBy[0]
          const isDesc = this.options.sortDesc && this.options.sortDesc[0]
          params.order_by = isDesc ? `-${sortField}` : sortField
        } else {
          // По умолчанию сортировка по дате создания (новые сверху)
          params.order_by = '-created_at'
        }

        const response = await api.getResults(this.formDataId, this.publicationId, params)
        this.list = (response.data || []).map((item) => ({
          ...item,
          ocr_result_id: item.parts?.[0]?.ocr_result_id,
        }))
        this.pagination.count = response.meta?.count || 0
      } catch (error) {
        this.$notice.errors(error)
      } finally {
        this.isLoading = false
      }
    },
    onRefresh() {
      this.loadData()
    },
    async onMoveToForm() {
      if (this.selected.length === 0) return

      // Разделяем на успешные и ошибочные записи
      const successItems = this.selected.filter((item) => item.status === 'SUCCESS')
      const errorItems = this.selected.filter((item) => item.status === 'ERROR')
      const insertedItems = this.selected.filter((item) => item.status === 'INSERTED')

      // Если все выделенные - это INSERTED или ERROR, не переносим
      if (successItems.length === 0) {
        let message = 'Невозможно перенести строки'
        if (insertedItems.length > 0 && errorItems.length > 0) {
          message += ' со статусом "Перенесено в форму" и "Предупреждение"'
        } else if (insertedItems.length > 0) {
          message += ' со статусом "Перенесено в форму"'
        } else if (errorItems.length > 0) {
          message += ' со статусом "Предупреждение"'
        }
        this.$notice.create({
          type: 'warning',
          text: message,
        })
        return
      }

      // Если есть ERROR или INSERTED записи, показываем предупреждение
      if (errorItems.length > 0 || insertedItems.length > 0) {
        let warningText = `Будут перенесены только ${successItems.length} успешных записей. `
        if (errorItems.length > 0) {
          warningText += `${errorItems.length} записей с предупреждениями будут пропущены. `
        }
        if (insertedItems.length > 0) {
          warningText += `${insertedItems.length} уже перенесены в форму.`
        }

        this.$notice.create({
          type: 'info',
          text: warningText,
        })
      }

      this.showTransferModal = true
      this.transferMessage = 'Перенос данных в форму...'
      this.transferProgress = 0

      try {
        // Формируем collectorUrl
        let collectorUrl = process.env.VUE_APP_COLLECTOR_API_URL || ''
        collectorUrl = collectorUrl.replace(/\/$/, '')

        // Переносим только успешные записи
        const dataToTransfer = successItems.map((item) => {
          const allValues = []
          const allImages = []
          const ocrResultIds = []

          if (item.parts && Array.isArray(item.parts)) {
            item.parts.forEach((part) => {
              if (part.ocr_result_id) {
                ocrResultIds.push(part.ocr_result_id)
              }

              // Формируем ссылки на изображения точно так же как в onViewDetails
              if (part.file_path) {
                let url = part.file_path
                if (!url.startsWith('http')) {
                  const path = url.startsWith('/') ? url : `/${url}`
                  url = `${collectorUrl}${path}`
                }
                allImages.push(url)
              }

              const rawData = part.data
              if (rawData) {
                try {
                  let parsedData = rawData
                  if (typeof rawData === 'string') {
                    parsedData = JSON.parse(rawData)
                  } else if (typeof rawData === 'object') {
                    parsedData = JSON.parse(JSON.stringify(rawData))
                  }
                  if (parsedData && parsedData.values && Array.isArray(parsedData.values)) {
                    allValues.push(...parsedData.values)
                  }
                } catch (error) {
                  // ignore error
                }
              }
            })
          }

          // Формируем объект согласно ScannableRowData
          const result = {
            values: allValues,
          }

          // image - Optional, добавляем только если есть изображения
          if (allImages.length > 0) {
            result.image = allImages
          }

          // ocr_result_ids — список id всех частей записи, бэк вернёт те, что реально вставил
          if (ocrResultIds.length > 0) {
            result.ocr_result_ids = ocrResultIds
          }

          return result
        })

        this.transferProgress = 30
        this.transferMessage = 'Обновление данных формы...'

        // Переносим данные в форму
        const formdataApi = new LkFormdataApi()
        const updateResponse = await formdataApi.updateScannable(this.formDataId, { data: dataToTransfer })

        this.transferProgress = 70
        this.transferMessage = 'Обновление статуса результатов...'

        // Обновляем статус только тех OCR-результатов, что бэк реально вставил в форму
        const insertedOcrResultIds = updateResponse?.data?.inserted_ocr_result_ids || []
        if (insertedOcrResultIds.length > 0) {
          const ocrApi = new CollectorOcrApi()
          await ocrApi.batchUpdateOcrResults({
            ocr_result_ids: insertedOcrResultIds,
            status: 'INSERTED',
          })
        }

        this.transferProgress = 100
        this.transferMessage = 'Перенос завершен!'

        this.$notice.create({
          type: 'success',
          text: `${successItems.length} записей успешно перемещено в форму`,
        })

        // Очищаем выбранные элементы и перезагружаем данные
        this.selected = []
        await this.loadData()
      } catch (error) {
        this.$notice.errors(error)
      } finally {
        // Закрываем модалку через небольшую задержку
        setTimeout(() => {
          this.showTransferModal = false
          this.transferProgress = 0
        }, 1000)
      }
    },
    onErrorFilterFocus() {
      const width = this.$refs.errorFilterWrapper?.offsetWidth
      this.errorMenuWidth = width ? `${width}px` : null
      this.loadErrorMessages()
    },
    async loadErrorMessages() {
      if (!this.formDataId) return
      this.isLoadingErrorMessages = true
      try {
        const api = new CollectorOcrApi()
        const response = await api.getErrorMessages(this.formDataId)
        this.errorMessageOptions = response.data || []
      } catch (error) {
        // silently ignore
      } finally {
        this.isLoadingErrorMessages = false
      }
    },
    onFilterChange() {
      this.options.page = 1
      this.loadData()
    },
    onErrorMessageClear() {
      this.filters.error_messages = []
      this.onFilterChange()
    },
    onRemoveErrorMessage(msg) {
      this.filters.error_messages = this.filters.error_messages.filter((m) => m !== msg)
      this.onFilterChange()
    },
    onParticipantCodeInput(value) {
      const cleaned = String(value || '').replace(/\D/g, '').slice(0, 5)
      this.filters.participant_code = cleaned || null
      this.onFilterChangeDebounced()
    },
    onParticipantCodeClear() {
      this.filters.participant_code = null
      this.onFilterChangeDebounced()
    },
    onFilterChangeDebounced() {
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout)
      }

      // Устанавливаем новый таймер на 500мс
      this.debounceTimeout = setTimeout(() => {
        this.options.page = 1
        this.loadData()
      }, 500)
    },
    onBack() {
      this.$emit('back')
    },
    async onDeleteResult(item) {
      if (!item.parts || item.parts.length === 0) {
        this.$notice.create({
          type: 'warning',
          text: 'Нет данных для удаления',
        })
        return
      }

      const propsData = {
        titleText: 'Удаление результата',
        mainText: 'Вы уверены, что хотите удалить результат распознавания?',
        successBtn: 'Удалить',
        cancelBtn: 'Отмена',
      }

      const modal = await createModalConfirm(this, propsData)
      const confirmed = await modal.open()
      await modal.close()

      if (!confirmed) return

      try {
        const api = new CollectorOcrApi()

        // Удаляем все части результата
        const deletePromises = item.parts.map((part) => {
          if (part.ocr_result_id) {
            return api.deleteFsdOcrData(
              this.formDataId,
              this.taskId,
              part.ocr_result_id,
            )
          }
          return Promise.resolve()
        })

        await Promise.all(deletePromises)

        this.$notice.create({
          type: 'success',
          text: 'Результат успешно удален',
        })

        // Обновляем список
        await this.loadData()
      } catch (error) {
        this.$notice.errors(error)
      }
    },
    async onDeleteSelected() {
      if (this.selected.length === 0) return

      const insertedItems = this.selected.filter((item) => item.status === 'INSERTED')
      if (insertedItems.length > 0) {
        return
      }

      const propsData = {
        titleText: 'Удаление результатов',
        mainText: 'Вы уверены, что хотите удалить результаты распознавания?',
        successBtn: 'Удалить все',
        cancelBtn: 'Отмена',
      }

      const modal = await createModalConfirm(this, propsData)
      const confirmed = await modal.open()
      await modal.close()

      if (!confirmed) return

      try {
        const api = new CollectorOcrApi()
        const deletePromises = []

        this.selected.forEach((item) => {
          if (item.parts && Array.isArray(item.parts)) {
            item.parts.forEach((part) => {
              if (part.ocr_result_id) {
                deletePromises.push(
                  api.deleteFsdOcrData(
                    this.formDataId,
                    this.taskId,
                    part.ocr_result_id,
                  ),
                )
              }
            })
          }
        })

        await Promise.all(deletePromises)

        this.$notice.create({
          type: 'success',
          text: `${this.selected.length} результат(ов) успешно удалено`,
        })

        // Очищаем выбранные элементы и перезагружаем данные
        this.selected = []
        await this.loadData()
      } catch (error) {
        this.$notice.errors(error)
      }
    },
    async onSaveParticipantCode(item) {
      const numValue = String(this.editingCodeValue).replace(/\D/g, '')
      if (numValue.length !== 5) {
        this.$notice.create({
          type: 'warning',
          text: 'Код участника должен содержать 5 цифр',
        })
        return
      }

      const participantCode = parseInt(numValue, 10)

      // Собираем values из ВСЕХ частей в один массив — как делает onViewDetails перед модалкой
      const allValues = []
      if (item.parts && Array.isArray(item.parts)) {
        item.parts.forEach((part) => {
          const rawData = part.data
          if (rawData) {
            try {
              let parsedData = rawData
              if (typeof rawData === 'string') {
                parsedData = JSON.parse(rawData)
              } else if (typeof rawData === 'object') {
                parsedData = JSON.parse(JSON.stringify(rawData))
              }
              if (parsedData && Array.isArray(parsedData.values)) {
                allValues.push(...parsedData.values)
              }
            } catch (e) { /* ignore */ }
          }
        })
      }
      const existingValues = allValues.length > 0 ? allValues : null

      try {
        const api = new CollectorOcrApi()

        if (item.parts && Array.isArray(item.parts) && item.parts.length > 0) {
          const firstPart = item.parts[0]

          // Обновляем values[0] только если там был предыдущий код участника
          const valuesToSend = existingValues !== null ? [...existingValues] : []
          const firstValueIsOldCode = valuesToSend.length > 0
            && String(valuesToSend[0]).replace(/\D/g, '') === String(item.participant_code)
          if (firstValueIsOldCode) {
            valuesToSend[0] = this.editingCodeValue
          }

          const firstPartPayload = {
            status: item.status,
            participant_code: participantCode,
            values: valuesToSend,
            error_message: 'Необходимо проверить и сохранить данные после изменения кода участника',
          }

          await api.updateOcrResult(firstPart.ocr_result_id, firstPartPayload)

          if (item.parts.length > 1) {
            const otherPartsPromises = item.parts.slice(1).map((part) => api.updateOcrResult(part.ocr_result_id, {
              status: item.status,
              values: [],
              participant_code: participantCode,
            }))
            await Promise.all(otherPartsPromises)
          }
        }

        this.$notice.create({
          type: 'success',
          text: 'Код участника успешно обновлён',
        })

        this.editingCodeValue = ''
        await this.loadData()
      } catch (error) {
        this.$notice.errors(error)
      }
    },
    async saveOcrResult(item, data) {
      const formValues = Object.values(data).filter(
        (val) => typeof val === 'string' || typeof val === 'number',
      )

      let foundEmpty = false
      let hasErrorAfterEmpty = false

      formValues.forEach((val) => {
        const isEmpty = val === '' || val === null || val === undefined

        if (isEmpty) {
          foundEmpty = true
        } else if (foundEmpty) {
          hasErrorAfterEmpty = true
        }
      })

      if (hasErrorAfterEmpty) {
        this.$notice.create({
          type: 'warning',
          text: 'Ячейки должны быть заполнены по порядку, без пропусков',
        })
        throw new Error('Ячейки должны быть заполнены по порядку, без пропусков')
      }

      // Проверяем что первое значение - это пятизначное число (participant_code)
      let participantCode = null
      if (formValues.length > 0) {
        const firstValue = formValues[0]
        const numValue = String(firstValue).replace(/\D/g, '')
        if (numValue.length === 5) {
          participantCode = parseInt(numValue, 10)
        }
      }

      const participantCodeChanged = participantCode !== null && participantCode !== item.participant_code

      if (participantCodeChanged) {
        const modal = await createModalConfirm(this, {
          titleText: 'Предупреждение!',
          mainText: 'Обязательно сравните все данные с изображением отсканированного бланка',
          cancelBtn: 'Продолжить заполнение',
          successBtn: 'Сохранить',
        })
        const confirmed = await modal.open()
        await modal.close()
        if (!confirmed) return false
      }

      try {
        const api = new CollectorOcrApi()

        if (item.parts && Array.isArray(item.parts) && item.parts.length > 0) {
          const firstPart = item.parts[0]

          const firstPartPayload = {
            status: 'SUCCESS',
            values: formValues,
          }
          if (participantCode !== null) {
            firstPartPayload.participant_code = participantCode
          }

          await api.updateOcrResult(firstPart.ocr_result_id, firstPartPayload)

          if (item.parts.length > 1) {
            const otherPartsPromises = item.parts.slice(1).map((part) => {
              const partPayload = {
                status: 'SUCCESS',
                values: [],
              }
              if (participantCode !== null) {
                partPayload.participant_code = participantCode
              }
              return api.updateOcrResult(part.ocr_result_id, partPayload)
            })

            await Promise.all(otherPartsPromises)
          }
        }

        this.$notice.create({
          type: 'success',
          text: 'Данные успешно обновлены',
        })

        const updatedParts = item.parts ? item.parts.map((part, idx) => ({
          ...part,
          data: { values: idx === 0 ? formValues : [] },
        })) : item.parts

        const updatedItem = {
          ...item,
          status: 'SUCCESS',
          error_message: null,
          participant_code: participantCode !== null ? participantCode : item.participant_code,
          parts: updatedParts,
        }

        const itemIndex = this.list.findIndex((listItem) => listItem.ocr_result_id === item.ocr_result_id)
        if (itemIndex !== -1) {
          this.$set(this.list, itemIndex, updatedItem)
        }

        await this.loadData()
      } catch (error) {
        this.$notice.errors(error)
        throw error
      }
    },
    formatDate(dateString) {
      return dateFormatter(dateString, 'DD.MM.YYYY HH:mm')
    },
    getRowClass(item) {
      if (item.status === 'INSERTED') {
        return 'disabled-row'
      }
      return ''
    },
    getStatusText(status) {
      const statusMap = {
        SUCCESS: 'Успешно',
        ERROR: 'Предупреждение',
        PENDING: 'В очереди',
        PROCESSING: 'Обработка',
        INSERTED: 'Перенесено в форму',
      }
      return statusMap[status] || status
    },
    getStatusColor(status) {
      const colorMap = {
        SUCCESS: 'success',
        ERROR: 'error',
        PENDING: 'info',
        PROCESSING: 'warning',
        INSERTED: 'primary',
      }
      return colorMap[status] || 'grey'
    },
    getStatusIcon(status) {
      const iconMap = {
        SUCCESS: 'mdi-check-circle',
        ERROR: 'mdi-alert-circle',
        PENDING: 'mdi-clock-outline',
        PROCESSING: 'mdi-sync',
        INSERTED: 'mdi-check-bold',
      }
      return iconMap[status] || 'mdi-help-circle'
    },
    async onViewDetails(item) {
      const headers = this.all_cells
        .filter(($cell) => $cell.is_scannable === true)
        .map(($cell, cell_uuid) => {
          const text = $cell.name || $cell.code
          return {
            $cell,
            text,
            hint: $cell.hint,
            value: `${cell_uuid}`,
            cell_uuid,
            sortable: false,
            slot: `header.${cell_uuid}`,
            width: $cell.width ? `${$cell.width}px` : null,
          }
        })
      const allValues = []
      const images = []
      let collectorUrl = process.env.VUE_APP_COLLECTOR_API_URL || ''
      collectorUrl = collectorUrl.replace(/\/$/, '')

      if (item.parts && Array.isArray(item.parts)) {
        item.parts.forEach((part) => {
          if (part.file_path) {
            let url = part.file_path
            if (!url.startsWith('http')) {
              const path = url.startsWith('/') ? url : `/${url}`
              url = `${collectorUrl}${path}`
            }
            images.push(url)
          }
          const rawData = part.data
          if (rawData) {
            try {
              let parsedData = rawData
              if (typeof rawData === 'string') {
                parsedData = JSON.parse(rawData)
              } else if (typeof rawData === 'object') {
                parsedData = JSON.parse(JSON.stringify(rawData))
              }
              if (parsedData && parsedData.values && Array.isArray(parsedData.values)) {
                allValues.push(...parsedData.values)
              }
            } catch (error) { /* ignore parse error */ }
          }
        })
      }
      const formData = {}
      if (images.length) {
        formData.image = images
      }
      let valueIndex = 0

      headers.forEach((header, cellIndex) => {
        if (valueIndex < allValues.length) {
          formData[cellIndex] = allValues[valueIndex]
          valueIndex += 1
        } else {
          formData[cellIndex] = ''
        }
      })

      const propsData = {
        headers,
        data: formData,
        index: 0,
        all_cells: this.all_cells,
        disabled: item.status === 'INSERTED',
        verticalLineData: {},
        verticalCellsData: headers.reduce((acc, header) => {
          acc[header.cell_uuid] = []
          return acc
        }, {}),
        headerDescriptionProcess: (header) => header?.$cell?.description,
        onSave: (data) => this.saveOcrResult(item, data),
        scheduleInformation: [],
      }


      const modal = await createLKPublicationsFormdataTableEditModal(this, propsData)
      await modal.open()
    },
  },
}
</script>

<style lang="scss" scoped>
.LKPublicationsFormdataOcrResultsTable {
  margin-top: 2rem;
  padding: 1rem 2rem;
}

::v-deep .disabled-row {
  opacity: 0.6;
  background-color: rgba(0, 0, 0, 0.02) !important;

  td {
    color: rgba(0, 0, 0, 0.54);
  }

  .v-input--selection-controls {
    pointer-events: none;
    opacity: 0.5;
  }
}

::v-deep .v-data-table-header th {
  white-space: nowrap;
}

::v-deep .v-autocomplete .v-select__selections {
  flex-wrap: nowrap;
  overflow: hidden;
}

::v-deep .participant-code-trigger {
  cursor: pointer;

  .participant-code-edit-icon {
    opacity: 0.4;
    vertical-align: middle;
  }
}
</style>


